import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { TabContainer as TabContainerType, TabState, createDatasetId } from '../../types';
import { usePaneLayout } from "../stores/hooks/usePaneLayout"
import { useProjectData } from "../stores/hooks/useProjectData"
import { shouldPersistDOM } from './TabRegistry';
import TabBar from './TabBar';
import TabContentRenderer from './TabContentRenderer';
import { TabCreationModal } from '../components/TabCreationModal';
import { createTabWithContext, getSelectableItems } from './tab-creation-helper';
import { getTabCreationOptions } from '../utils/tab-creation-utils';
import { TabHelp, tabHelpContent } from '../components/ContextHelp/helpContent';
import { createNewReport } from '../utils/reportUtils';
import logger from '../utils/logger';
import * as styles from './TabContainer.css.ts';
import { vars } from '../theme/theme.css';

/**
 * TabContainer (新バージョン)
 * 
 * 簡素化されたTabContainer
 * - Props Drillingを排除
 * - TabBarとTabContentを分離
 * - 各タブが自身で必要なデータを取得
 */

interface TabContainerProps {
  container: TabContainerType;
  isActive?: boolean;
  onTabDragStart?: (tabId: string, containerId: string, event: React.DragEvent) => void;
  onTabDragEnd?: () => void;
  onTabDrop?: (draggedTabId: string, targetContainerId: string, dropIndex?: number) => void;
}

export const TabContainer: React.FC<TabContainerProps> = ({
  container,
  isActive = false,
  onTabDragStart,
  onTabDragEnd,
  onTabDrop
}) => {
  const { activateTab, closeTab, addTab } = usePaneLayout();
  const { datasets, project, updateProject } = useProjectData();
  const reports = useMemo(() => project?.reports || [], [project?.reports]);
  const [showTabCreationModal, setShowTabCreationModal] = useState(false);
  const [highlightingTabId, setHighlightingTabId] = useState<string | null>(null);
  
  const activeTab = container.tabs.find(tab => tab.id === container.activeTabId);
  
  // タブクリックハンドラー
  const handleTabClick = useCallback((tabId: string) => {
    activateTab(container.id, tabId);
  }, [activateTab, container.id]);
  
  // タブクローズハンドラー
  const handleTabClose = useCallback((tabId: string) => {
    closeTab(container.id, tabId);
  }, [closeTab, container.id]);
  
  // ドラッグ開始ハンドラー
  const handleDragStart = useCallback((tabId: string, event: React.DragEvent) => {
    onTabDragStart?.(tabId, container.id, event);
  }, [onTabDragStart, container.id]);
  
  // タブドロップハンドラー（タブバー内での並び替え）
  const handleTabDrop = useCallback((draggedTabId: string, targetIndex: number) => {
    onTabDrop?.(draggedTabId, container.id, targetIndex);
  }, [onTabDrop, container.id]);
  
  // コンテナドロップハンドラー（他のペインからのドロップ）
  const handleContainerDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const draggedTabId = event.dataTransfer.getData('text/plain');
    if (draggedTabId && draggedTabId !== '') {
      onTabDrop?.(draggedTabId, container.id);
    }
  }, [onTabDrop, container.id]);
  
  // ペインのフォーカス時にアクティブ化
  const handlePaneFocus = useCallback(() => {
    if (container.activeTabId) {
      activateTab(container.id, container.activeTabId);
    }
  }, [activateTab, container.id, container.activeTabId]);
  
  // タブタイトルのカスタマイズ（データセット名を含める）
  const getTabTitle = useCallback((tab: TabState): string => {
    let title = tab.title;
    
    // data-table, statisticsタブの場合、データセット名を追加
    if ((tab.type === 'data-table' || tab.type === 'statistics') && tab.metadata?.datasetId) {
      const dataset = datasets[tab.metadata.datasetId];
      if (dataset) {
        if (tab.title !== dataset.name) {
          title = `${tab.title} - ${dataset.name}`;
        } else {
          title = dataset.name;
        }
      }
    }
    
    // タブタイプのプレフィックスを追加
    if (tab.type === 'data-table') {
      return `Data Table: ${title}`;
    } else if (tab.type === 'report') {
      return `Report: ${title}`;
    }
    
    return title;
  }, [datasets]);
  
  
  // タブハイライトイベントのリスナー
  useEffect(() => {
    const handleHighlightTab = (event: Event) => {
      const customEvent = event as CustomEvent<{ tabId: string }>;
      const { tabId } = customEvent.detail;
      const tab = container.tabs.find(t => t.id === tabId);
      if (tab) {
        setHighlightingTabId(tabId);
        // 1秒後にハイライトを解除
        setTimeout(() => {
          setHighlightingTabId(null);
        }, 1000);
      }
    };
    
    window.addEventListener('highlightTab', handleHighlightTab);
    return () => {
      window.removeEventListener('highlightTab', handleHighlightTab);
    };
  }, [container.tabs]);
  
  
  // タブ追加ハンドラー
  const handleAddTab = useCallback(() => {
    setShowTabCreationModal(true);
  }, []);
  
  // 選択可能なアイテムを取得
  const getSelectableItemsForModal = useCallback((optionId: string) => {
    const context = {
      datasets,
      reports,
      activeDatasetId: undefined // この場合は不要
    };
    
    return getSelectableItems(optionId, context);
  }, [datasets, reports]);
  
  // タブオプション選択ハンドラー
  const handleSelectTabOption = useCallback((optionId: string, selectedItemId?: string) => {
    const activeDatasetIdString = container.tabs.find(tab => tab.type === 'data-table')
      ?.metadata?.datasetId || Object.keys(datasets)[0];
    const activeDatasetId = activeDatasetIdString ? createDatasetId(activeDatasetIdString) : undefined;

    // 新規レポート作成の場合、Reportオブジェクトも作成
    let updatedReports = reports;
    if (optionId === 'report' && selectedItemId === '__new__' && project) {
      const newReport = createNewReport('New Report');
      updateProject((currentProject) => ({
        ...currentProject,
        reports: [...(currentProject.reports || []), newReport]
      }));
      updatedReports = [...reports, newReport];
      // selectedItemIdを新しいレポートIDに置き換え
      selectedItemId = newReport.id;
      logger.info('[TabContainer] Created new report:', { reportId: newReport.id });
    }

    // 共通のタブ作成ヘルパーを使用
    const context = {
      datasets,
      reports: updatedReports,
      activeDatasetId
    };

    const result = createTabWithContext(optionId, context, selectedItemId);

    if (result.tab) {
      addTab(container.id, result.tab);
    } else if (result.error) {
      logger.error('[TabContainer] Failed to create tab:', { error: result.error });
    }

    setShowTabCreationModal(false);
  }, [container.id, container.tabs, datasets, reports, project, updateProject, addTab]);
  
  // タブ作成オプションを取得
  const tabCreationOptions = getTabCreationOptions(Object.keys(datasets).length > 0);
  
  return (
    <div
      className={`${styles.tabContainer} ${isActive ? styles.activeContainer : ''}`}
      data-testid="tab-container"
      data-container-id={container.id}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
      onDrop={handleContainerDrop}
      onMouseDown={handlePaneFocus}
      tabIndex={-1}
      style={{
        '--color-background': vars.color.background.primary,
        '--color-background-secondary': vars.color.background.secondary,
        '--color-background-hover': vars.color.background.hover,
        '--color-border': vars.color.border.primary,
        '--color-primary': vars.color.primary,
        '--color-primary-dark': vars.color.primaryDark,
        '--color-primary-light': `${vars.color.primaryLight}33`,
        '--color-success': vars.color.success,
        '--color-success-dark': vars.color.primaryDark,
        '--color-text': vars.color.text.primary,
        '--color-text-secondary': vars.color.text.secondary,
        '--color-text-muted': vars.color.text.muted,
        '--color-text-primary': vars.color.text.primary
      } as React.CSSProperties}
    >
      {/* タブバー */}
      <TabBar
        tabs={container.tabs}
        activeTabId={container.activeTabId}
        onTabClick={handleTabClick}
        onTabClose={handleTabClose}
        onTabDragStart={handleDragStart}
        onTabDragEnd={onTabDragEnd}
        onTabDrop={handleTabDrop}
        getTabTitle={getTabTitle}
        highlightingTabId={highlightingTabId}
        onAddTab={handleAddTab}
        helpContent={activeTab && tabHelpContent[activeTab.type] ? <TabHelp tabType={activeTab.type} /> : undefined}
        helpAriaLabel={activeTab && tabHelpContent[activeTab.type] ? tabHelpContent[activeTab.type].ariaLabel : undefined}
      />
      
      {/* タブコンテンツ */}
      <div className={styles.tabContent} data-testid="tab-content">
        {/* DOM永続化が必要なタブは常にマウント */}
        {container.tabs
          .filter(tab => shouldPersistDOM(tab.type))
          .map(tab => (
            <div
              key={tab.id}
              data-tab-id={tab.id}
              data-tab-type={tab.type}
              data-tab-title={tab.title}
              style={{
                display: tab.id === container.activeTabId ? 'flex' : 'none',
                flexDirection: 'column',
                height: '100%'
              }}
            >
              <TabContentRenderer tab={tab} containerId={container.id} />
            </div>
          ))
        }
        
        {/* 非永続化タブはアクティブな時のみマウント */}
        {activeTab && !shouldPersistDOM(activeTab.type) && (
          <div
            data-tab-id={activeTab.id}
            data-tab-type={activeTab.type}
            data-tab-title={activeTab.title}
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
          >
            <TabContentRenderer tab={activeTab} containerId={container.id} />
          </div>
        )}
      </div>
      
      {/* タブ作成モーダル */}
      <TabCreationModal
        isOpen={showTabCreationModal}
        onClose={() => setShowTabCreationModal(false)}
        options={tabCreationOptions}
        onSelectOption={handleSelectTabOption}
        getSelectableItems={getSelectableItemsForModal}
      />
    </div>
  );
};

export default React.memo(TabContainer);